import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-python';

interface PrismCodeBlockProps {
  title: string;
  code: string;
  language?: string;
  icon?: React.ComponentType<{ className?: string }>;
  defaultExpanded?: boolean;
  description?: string;
}

export const PrismCodeBlock: React.FC<PrismCodeBlockProps> = ({
  title,
  code,
  language = 'python',
  icon: Icon,
  defaultExpanded = false,
  description
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    // Highlight code when component mounts or code changes
    Prism.highlightAll();
  }, [code, isExpanded]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setIsCopied(true);
      toast({
        title: "Copied to clipboard",
        description: `${title} code copied successfully`,
      });
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="border border-slate-600 rounded-lg overflow-hidden bg-slate-800">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 bg-slate-700 cursor-pointer hover:bg-slate-650 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-slate-300" />
            ) : (
              <ChevronRight className="h-4 w-4 text-slate-300" />
            )}
            {Icon && <Icon className="h-4 w-4 text-blue-400" />}
            <h3 className="font-medium text-white">{title}</h3>
          </div>
          {description && (
            <span className="text-sm text-slate-400">— {description}</span>
          )}
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard();
          }}
          className="text-slate-300 hover:text-white hover:bg-slate-600"
        >
          {isCopied ? (
            <Check className="h-4 w-4" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Code Content */}
      {isExpanded && (
        <div className="relative">
          <pre className="!m-0 !bg-slate-900 overflow-x-auto">
            <code className={`language-${language} !text-sm`}>
              {code}
            </code>
          </pre>
          
          {/* Copy button overlay */}
          <Button
            variant="ghost"
            size="sm"
            onClick={copyToClipboard}
            className="absolute top-2 right-2 text-slate-400 hover:text-white hover:bg-slate-700 opacity-70 hover:opacity-100"
          >
            {isCopied ? (
              <Check className="h-3 w-3" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

// Collection of multiple code blocks with expand/collapse all functionality
interface PrismCodeCollectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

export const PrismCodeCollection: React.FC<PrismCodeCollectionProps> = ({
  title,
  description,
  children
}) => {
  const [allExpanded, setAllExpanded] = useState(false);

  const toggleAll = () => {
    setAllExpanded(!allExpanded);
    // Trigger expand/collapse on all child components
    // This would require updating the PrismCodeBlock to accept external control
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          {description && (
            <p className="text-sm text-slate-400 mt-1">{description}</p>
          )}
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={toggleAll}
          className="border-slate-600 text-slate-300 hover:bg-slate-700"
        >
          {allExpanded ? 'Collapse All' : 'Expand All'}
        </Button>
      </div>
      
      <div className="space-y-3">
        {children}
      </div>
    </div>
  );
}; 